import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';

const EMPTY = { title: '', excerpt: '', content: '', cover_image: '', tags: [], author: 'Cespedis Editorial', status: 'published' };

export default function AdminBlogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagText, setTagText] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/blog', { params: { status: 'all' } }).then((res) => setItems(res.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/blog/${editing.id}`, form); toast.success('Post updated'); }
      else { await api.post('/blog', form); toast.success('Post created'); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save'); }
  };

  const onDelete = async () => {
    try { await api.delete(`/blog/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch { toast.error('Could not delete'); }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-semibold text-[#144726]">Blog posts</h1><p className="mt-1 text-sm text-slate-500">Publish and manage editorial content shown on the public blog.</p></div>
        <Button onClick={openCreate} className="bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="admin-add-post-button"><Plus className="mr-2 h-4 w-4" /> New post</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="px-4 py-3 text-left font-semibold">Title</th>
              <th className="px-4 py-3 text-left font-semibold">Author</th>
              <th className="px-4 py-3 text-left font-semibold">Tags</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Published</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="px-4 py-6 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-slate-500">No posts.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-[#144726] max-w-md">{p.title}</td>
                  <td className="px-4 py-3 text-slate-600">{p.author}</td>
                  <td className="px-4 py-3 text-slate-600"><div className="flex flex-wrap gap-1">{p.tags?.map((t) => <Badge key={t} className="rounded-md bg-slate-100 text-slate-700 border-0">{t}</Badge>)}</div></td>
                  <td className="px-4 py-3"><Badge className={`rounded-md border-0 ${p.status === 'published' ? 'bg-[#C6A35B] text-[#144726]' : 'bg-slate-100 text-slate-700'}`}>{p.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(p.published_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-2 border-slate-200" onClick={() => openEdit(p)} data-testid="admin-post-edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteId(p.id)} data-testid="admin-post-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit post' : 'New post'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-form-title" /></div>
            <div><Label>Excerpt</Label><Textarea rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div><Label>Cover image URL</Label><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Author</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2"><Input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="Add a tag" /><Button type="button" variant="outline" onClick={() => { if (tagText.trim()) { setForm({ ...form, tags: [...form.tags, tagText.trim()] }); setTagText(''); } }}>Add</Button></div>
              <div className="mt-2 flex flex-wrap gap-2">{form.tags.map((a, i) => (<span key={i} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700">{a}<button onClick={() => setForm({ ...form, tags: form.tags.filter((_, idx) => idx !== i) })}><X className="h-3 w-3" /></button></span>))}</div>
            </div>
            <div><Label>Content (Markdown supported)</Label><Textarea rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="font-mono text-sm" data-testid="post-form-content" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="post-form-save">{editing ? 'Save changes' : 'Publish'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete post?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
